/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth.service';
import {
	EMPTY,
	interval,
	switchMap,
	from,
	Observable,
	of,
	Subject,
	race,
	timer,
	BehaviorSubject,
	merge,
	TimeoutError,
} from 'rxjs';
import { catchError, filter, map, take, tap, timeout } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import dayjs from 'dayjs';
import {
	SSO_VERIFICATION_BUTTON,
	SSO_VERIFICATION_SUCCESS,
	SSO_VERIFICATION_TITLE,
} from '@touchplan/strings';

enum ReauthMethod {
	Redirect = 'redirect',
	Popup = 'popup',
}

/** time between checks */
const recheckInterval = 1000 * 60 * 60; // 1000ms * 60 sec * 60 min = 1 hour
// const recheckInterval = 1000 * 60;
/** time for warning banner to display, must be less than the recheck interval */
const warningInterval = 1000 * 60 * 5; // 5 min
// const warningInterval = 1000 * 10;
/** how long the user has to enter their credentials (if necessary) */
const recheckTimeout = 1000 * 60 * 5; // 5 min
// const recheckTimeout = 1000 * 30;

@Injectable({
	providedIn: 'root',
})
export class ForcedSsoReauthService {
	private _started = false;

	private authService = inject(AuthService);
	private snackbar = inject(MatSnackBar);

	private manualReauth$ = new Subject<void>();
	private _showBanner$ = new BehaviorSubject(false);
	public showBanner$ = this._showBanner$.asObservable();

	private redirectResultHandling() {
		//still undecided if this should be subscribed to here or returned
		//it's kinda awkward to combine the results of redirect and popup
		//with how radically differently they return their results
		from(this.authService.getRedirectResult())
			.pipe(
				//maybe also check `result.operationType === "reauthenticate"
				filter((result) => result !== null), //null means no redirect was done
				map((result) => {
					// console.log('redirect result', result);
					return null;
				}),
				catchError((error) => of(error))
			)
			.subscribe((error) => {
				this.handleReauthResult(error);
			});
	}

	private handleReauthResult(error: Error | undefined) {
		if (error) {
			this.authService.logout();
		} else {
			//no op
			this.displaySuccess();
		}
	}

	/**
	 * starts the listers on the single instace of this service.
	 * This service does not support multiple instances or being stopped and expects
	 * to run until the app is closed.
	 */
	public start() {
		if (this._started) {
			throw new Error('ForcedSsoReauthService already started');
		}
		this._started = true;

		this.redirectResultHandling();
		//do a thing
		const reauthTime$ = this.authService.isUserSSO().pipe(
			switchMap((isSSO) => {
				if (!isSSO) {
					return EMPTY;
				}
				return interval(recheckInterval - warningInterval);
			})
		);

		//show the banner shortly before refresh, then hide it when the timer expires
		reauthTime$
			.pipe(
				switchMap(() => {
					return merge(of(true), timer(warningInterval).pipe(map(() => false)));
				})
			)
			.subscribe(this._showBanner$);

		reauthTime$
			.pipe(
				switchMap(() => {
					//only run one of "a user triggered popup reauth" or "a timer triggered redirect reauth"
					return race(
						//can kind of work on this locally by:
						// - changing redirect to popup
						// - enabling localhost to show popups unconditionally chrome://settings/content/popups
						timer(warningInterval).pipe(map(() => ReauthMethod.Redirect)),
						this.manualReauth$.pipe(
							take(1),
							map(() => ReauthMethod.Popup)
						)
					).pipe(
						switchMap((method) => this.revalidateIdentityProviderAuth(method)),
						timeout(recheckTimeout),
						catchError((error) => {
							if (error instanceof TimeoutError) {
								return of(error);
							} else throw error;
						})
					);
				})
			)
			.subscribe((error) => {
				this.handleReauthResult(error);
			});

		this.prepVisuals();
	}

	/**
	 * trigger an idP reauth now, resets the the timer
	 */
	public revalidateIdentityProviderAuthWithPopup() {
		//can add method = ReauthMethod.Popup to the signature if we want on demand redirect reauth
		this.manualReauth$.next();
	}

	private revalidateIdentityProviderAuth(method: ReauthMethod): Observable<null | Error> {
		const currentUserProviderId = this.authService.currentProviderId;
		if (!currentUserProviderId) {
			// alternatively throw an error
			return EMPTY;
		}

		if (method === ReauthMethod.Redirect) {
			this.authService.reauthenticateWithRedirect(currentUserProviderId);
			return EMPTY;
		} else if (method === ReauthMethod.Popup) {
			return from(this.authService.reauthenticateWithPopup(currentUserProviderId)).pipe(
				map(() => null),
				catchError((error) => of(error))
			);
		}
	}

	//kept seperate in case we ever want to seperate the logic and the ui
	private prepVisuals() {
		this.showBanner$
			.pipe(
				switchMap((show) => {
					if (show) {
						const time = dayjs().add(warningInterval, 'ms').format('HH:mm');

						const ref = this.snackbar.open(SSO_VERIFICATION_TITLE(time), SSO_VERIFICATION_BUTTON, {
							horizontalPosition: 'center',
							verticalPosition: 'top',
							duration: warningInterval,
						});
						return ref.onAction();
					} else {
						this.snackbar.dismiss();
						return EMPTY;
					}
				})
			)
			.subscribe(() => {
				this.revalidateIdentityProviderAuthWithPopup();
			});
	}

	private displaySuccess() {
		this.snackbar.open(SSO_VERIFICATION_SUCCESS, '', {
			horizontalPosition: 'center',
			verticalPosition: 'top',
			duration: 2000,
		});
	}
}
